import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { resolveAttachmentIcon } from '../../../notifications/utils/notification-attachment-icon.util';
import { MESSAGE_COMPOSER_BLOCK_OPTIONS, MESSAGE_COMPOSER_TOOLBAR_OPTIONS } from './message-composer.config';
import {
  createFormattedComposerContent,
  EMPTY_EDITOR_MARKER,
  findComposerMarker,
  placeComposerCaretAfter,
  placeComposerCaretAfterMarker,
  placeComposerCaretAtEnd,
  readComposerValue,
  selectComposerNodeContents,
} from './message-composer-editor.helper';
import {
  MessageComposerBlockFormat,
  MessageComposerFormat,
  MessageComposerFormatCommand,
  MessageComposerToolbarOption,
} from './message-composer-editor.types';
import { CoreUiMessageComposerAttachment, CoreUiMessageComposerPersistedAttachment } from './message-composer.types';

const DEFAULT_MAX_ATTACHMENT_SIZE_MB = 5;
const DEFAULT_MAX_ATTACHMENTS = 4;
const DEFAULT_FILE_ACCEPT = 'image/*';
const WILDCARD_CONTENT_TYPE_SUFFIX = '/*';

type ComposerCommandSelection = 'existing' | 'content' | 'marker';

interface ComposerAttachmentView {
  id: string;
  name: string;
  contentType: string;
  previewUrl: string | null;
}

@Component({
  selector: 'core-ui-message-composer',
  templateUrl: './message-composer.component.html',
  styleUrls: ['./message-composer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MessageComposerComponent),
      multi: true,
    },
  ],
})
export class MessageComposerComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @ViewChild('editor') private editorRef?: ElementRef<HTMLDivElement>;

  @Input() label = 'Mensaje';
  @Input() placeholder = 'Escribe el mensaje personalizado';
  @Input() maxLength = 1000;
  @Input() minHeight = 160;
  @Input() readOnly = false;
  @Input() allowAttachments = true;
  @Input() allowAnyFile = false;
  @Input() fileAccept = DEFAULT_FILE_ACCEPT;
  @Input() attachmentPickerTooltip = 'Adjuntar';
  @Input() attachmentPickerAriaLabel = 'Adjuntar archivos';
  @Input() attachmentsPersisted = true;
  @Input() maxAttachments = DEFAULT_MAX_ATTACHMENTS;
  @Input() maxAttachmentSizeMb = DEFAULT_MAX_ATTACHMENT_SIZE_MB;
  @Input() maxImageAttachmentSizeMb?: number;
  @Input() maxVideoAttachmentSizeMb?: number;
  @Input() maxDocumentAttachmentSizeMb?: number;
  @Input()
  set persistedAttachments(value: readonly CoreUiMessageComposerPersistedAttachment[]) {
    this._persistedAttachments = [...(value ?? [])];
  }

  get persistedAttachments(): readonly CoreUiMessageComposerPersistedAttachment[] {
    return this._persistedAttachments;
  }

  @Output() attachmentsChange = new EventEmitter<readonly File[]>();
  @Output() persistedAttachmentsChange = new EventEmitter<readonly CoreUiMessageComposerPersistedAttachment[]>();

  value = '';
  disabled = false;
  isFocused = false;
  attachmentError = '';
  attachments: CoreUiMessageComposerAttachment[] = [];
  blockType = 'p';
  readonly activeFormats = new Set<MessageComposerFormat>();
  readonly blockOptions = [...MESSAGE_COMPOSER_BLOCK_OPTIONS];
  readonly toolbarOptions = MESSAGE_COMPOSER_TOOLBAR_OPTIONS;

  private attachmentSequence = 0;
  private _persistedAttachments: CoreUiMessageComposerPersistedAttachment[] = [];
  private pendingHtmlValue: {
    html: string | null | undefined;
    fallbackText: string;
  } | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  get isReadOnly(): boolean {
    return this.readOnly || this.disabled;
  }

  get characterCount(): number {
    return this.value.length;
  }

  get canAddAttachments(): boolean {
    return this.allowAttachments && !this.isReadOnly && this.attachmentItems.length < this.maxAttachments;
  }

  get attachmentItems(): ComposerAttachmentView[] {
    return [
      ...this.persistedAttachments.map(attachment => ({
        id: attachment.id,
        name: attachment.name,
        contentType: attachment.contentType ?? '',
        previewUrl: attachment.previewUrl ?? null,
      })),
      ...this.attachments.map(attachment => ({
        id: attachment.id,
        name: attachment.file.name,
        contentType: attachment.file.type,
        previewUrl: attachment.previewUrl,
      })),
    ];
  }

  get hasPendingFormatting(): boolean {
    return this.activeFormats.size > 0 || this.blockType !== 'p';
  }

  get showPlaceholder(): boolean {
    return !this.value && !this.hasPendingFormatting;
  }

  ngAfterViewInit(): void {
    this.renderValue();
    if (!this.pendingHtmlValue) return;

    const pending = this.pendingHtmlValue;
    this.pendingHtmlValue = null;
    this.writeHtmlValue(pending.html, pending.fallbackText);
  }

  ngOnDestroy(): void {
    this.releaseAttachments();
  }

  writeValue(value: string | null): void {
    this.value = value ?? '';
    this.renderValue();

    if (!this.value && this.attachments.length) {
      this.clearAttachments();
    }
  }

  writeHtmlValue(html: string | null | undefined, fallbackText = ''): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor) {
      this.pendingHtmlValue = { html, fallbackText };
      this.value = fallbackText;
      return;
    }

    if (!html?.trim()) {
      this.writeValue(fallbackText);
      return;
    }

    editor.innerHTML = html;
    this.value = readComposerValue(editor);
    if (!this.value && fallbackText) {
      this.value = fallbackText;
    }
    if (!this.value && this.attachments.length) {
      this.clearAttachments();
    }
    this.refreshFormatState();
    this.emitValue();
  }

  getHtmlValue(): string | null {
    const editor = this.editorRef?.nativeElement;
    if (!editor || !this.value.trim()) return null;

    return editor.innerHTML.trim() || null;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleBeforeInput(event: InputEvent): void {
    if (this.isReadOnly || event.inputType !== 'insertText' || !event.data) return;

    const availableLength = Math.max(0, this.maxLength - this.value.length);
    const requiresPendingFormat = !this.value && this.hasPendingFormatting;
    const exceedsAvailableLength = event.data.length > availableLength;
    if (!requiresPendingFormat && !exceedsAvailableLength) return;

    event.preventDefault();
    this.insertText(event.data);
  }

  handleInput(preserveEmptyFormatting = false): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor || this.isReadOnly) return;

    const nextValue = readComposerValue(editor);
    if (nextValue.length > this.maxLength) {
      this.value = nextValue.slice(0, this.maxLength);
      this.renderValue();
      placeComposerCaretAtEnd(this.document, editor);
    } else {
      this.value = nextValue;
    }

    if (this.value || preserveEmptyFormatting) {
      this.refreshFormatState();
    } else {
      this.resetEditorFormatting(editor);
    }

    this.emitValue();
  }

  handleFocus(): void {
    if (this.isReadOnly) return;
    this.isFocused = true;
    this.refreshFormatState();
  }

  handleBlur(): void {
    this.isFocused = false;
    this.onTouched();
  }

  handlePaste(event: ClipboardEvent): void {
    if (this.isReadOnly) return;

    event.preventDefault();
    this.insertText(event.clipboardData?.getData('text/plain') ?? '');
  }

  handleDrop(event: DragEvent): void {
    event.preventDefault();
    if (this.isReadOnly) return;

    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length) {
      this.addAttachments(files);
      return;
    }

    this.insertText(event.dataTransfer?.getData('text/plain') ?? '');
  }

  applyFormat(command: MessageComposerFormatCommand): void {
    if (this.isReadOnly) return;

    const shouldActivate = !this.isFormatActive(command);
    const commandSelection = this.prepareEditorForCommand();
    this.document.execCommand(command, false);
    this.restoreSelectionAfterCommand(commandSelection);
    this.syncAfterCommand(true);

    if (!this.value) {
      this.updateEmptyFormatState(command, shouldActivate);
    }
  }

  isFormatActive(format: MessageComposerFormat): boolean {
    return this.activeFormats.has(format);
  }

  refreshFormatState(): void {
    if (!this.value && (this.activeFormats.size || this.blockType !== 'p')) return;

    this.activeFormats.clear();
    if (this.isReadOnly) return;

    const editor = this.editorRef?.nativeElement;
    const selection = this.document.getSelection();
    if (!editor || !selection?.anchorNode || !editor.contains(selection.anchorNode)) return;

    const commands: MessageComposerFormatCommand[] = [
      'bold',
      'italic',
      'underline',
      'insertUnorderedList',
      'insertOrderedList',
    ];

    commands.forEach(command => {
      if (this.document.queryCommandState(command)) {
        this.activeFormats.add(command);
      }
    });

    const queriedBlock = String(this.document.queryCommandValue('formatBlock')).replace(/[<>]/g, '').toLowerCase();
    const currentBlock = ['h3', 'blockquote', 'pre'].includes(queriedBlock) ? queriedBlock : 'p';

    this.blockType = currentBlock;

    if (currentBlock === 'blockquote' || currentBlock === 'pre') {
      this.activeFormats.add(currentBlock);
    }
  }

  applyBlockValue(block: string): void {
    if (this.isReadOnly) return;

    this.blockType = block;
    const commandSelection = this.prepareEditorForCommand();
    this.document.execCommand('formatBlock', false, block);
    this.restoreSelectionAfterCommand(commandSelection);
    this.syncAfterCommand(true);

    if (!this.value) {
      this.updateEmptyBlockState(block);
    }
  }

  toggleBlockValue(block: MessageComposerBlockFormat): void {
    this.applyBlockValue(this.isFormatActive(block) ? 'p' : block);
  }

  toggleToolbarOption(option: MessageComposerToolbarOption): void {
    if (option.type === 'block') {
      this.toggleBlockValue(option.value);
      return;
    }

    this.applyFormat(option.value);
  }

  trackToolbarOption(_index: number, option: MessageComposerToolbarOption): MessageComposerFormat {
    return option.value;
  }

  trackAttachment(_index: number, attachment: ComposerAttachmentView): string {
    return attachment.id;
  }

  clearMessage(): void {
    if (this.isReadOnly || (!this.value && !this.attachmentItems.length)) return;

    this.value = '';
    this.renderValue();
    this.clearAttachments();
    this.clearPersistedAttachments();
    this.emitValue();
    this.focusEditor();
  }

  openAttachmentPicker(input: HTMLInputElement): void {
    if (!this.canAddAttachments) return;
    input.click();
  }

  selectAttachments(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.addAttachments(files);
  }

  removeAttachment(id: string): void {
    if (this.isReadOnly) return;

    const attachment = this.attachments.find(item => item.id === id);
    if (!attachment) {
      const remaining = this.persistedAttachments.filter(item => item.id !== id);
      if (remaining.length === this.persistedAttachments.length) return;

      this._persistedAttachments = remaining;
      this.attachmentError = '';
      this.persistedAttachmentsChange.emit(this._persistedAttachments);
      return;
    }

    this.revokePreviewUrl(attachment.previewUrl);
    this.attachments = this.attachments.filter(item => item.id !== id);
    this.attachmentError = '';
    this.emitAttachments();
  }

  private addAttachments(files: File[]): void {
    if (!this.canAddAttachments || !files.length) return;

    this.attachmentError = '';
    const availableSlots = Math.max(0, this.maxAttachments - this.attachmentItems.length);
    const supportedFiles = files.filter(file => this.isAcceptedFile(file));
    const oversizedFile = supportedFiles.find(file => file.size > this.maxAttachmentSizeBytes(file));
    const validFiles = supportedFiles.filter(file => file.size <= this.maxAttachmentSizeBytes(file));
    const acceptedFiles = validFiles.slice(0, availableSlots);

    if (supportedFiles.length !== files.length) {
      this.attachmentError = 'Tipo de archivo no permitido.';
    } else if (oversizedFile) {
      this.attachmentError = `Tamaño máximo: ${this.maxAttachmentSizeFor(oversizedFile)} MB.`;
    } else if (validFiles.length > availableSlots) {
      this.attachmentError = `Máximo ${this.maxAttachments} archivos.`;
    }

    const additions = acceptedFiles.map(file => ({
      id: `composer-attachment-${++this.attachmentSequence}`,
      file,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));

    this.attachments = [...this.attachments, ...additions];
    this.emitAttachments();
  }

  private maxAttachmentSizeBytes(file: File): number {
    return this.maxAttachmentSizeFor(file) * 1024 * 1024;
  }

  private maxAttachmentSizeFor(file: File): number {
    if (file.type.startsWith('image/')) {
      return this.maxImageAttachmentSizeMb ?? this.maxAttachmentSizeMb;
    }
    if (file.type.startsWith('video/')) {
      return this.maxVideoAttachmentSizeMb ?? this.maxAttachmentSizeMb;
    }
    return this.maxDocumentAttachmentSizeMb ?? this.maxAttachmentSizeMb;
  }

  private syncAfterCommand(preserveEmptyFormatting = false): void {
    this.handleInput(preserveEmptyFormatting);
    this.focusEditor();
  }

  private focusEditor(): void {
    this.editorRef?.nativeElement.focus();
  }

  private prepareEditorForCommand(): ComposerCommandSelection {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return 'existing';

    this.focusEditor();
    if (this.value) {
      const selection = this.document.getSelection();
      const hasSelectedEditorContent =
        !!selection?.anchorNode &&
        !!selection.focusNode &&
        editor.contains(selection.anchorNode) &&
        editor.contains(selection.focusNode) &&
        !selection.isCollapsed;

      if (hasSelectedEditorContent) return 'existing';

      selectComposerNodeContents(this.document, editor);
      return 'content';
    }

    const existingMarker = findComposerMarker(editor);
    if (existingMarker) {
      placeComposerCaretAfterMarker(this.document, existingMarker);
      return 'existing';
    }

    const marker = this.document.createTextNode(EMPTY_EDITOR_MARKER);
    editor.replaceChildren(marker);

    selectComposerNodeContents(this.document, marker);
    return 'marker';
  }

  private restoreSelectionAfterCommand(commandSelection: ComposerCommandSelection): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor || commandSelection === 'existing') return;

    if (commandSelection === 'marker') {
      placeComposerCaretAfterMarker(this.document, findComposerMarker(editor));
      return;
    }

    placeComposerCaretAtEnd(this.document, editor);
  }

  private renderValue(): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return;

    if (!this.value) {
      this.resetEditorFormatting(editor);
      return;
    }

    if (readComposerValue(editor) === this.value) return;
    editor.textContent = this.value;
  }

  private resetEditorFormatting(editor: HTMLElement): void {
    editor.textContent = '';
    this.blockType = 'p';
    this.activeFormats.clear();

    if (this.isFocused) {
      placeComposerCaretAtEnd(this.document, editor);
    }
  }

  private updateEmptyFormatState(format: MessageComposerFormatCommand, shouldActivate: boolean): void {
    if (format === 'insertUnorderedList' || format === 'insertOrderedList') {
      this.activeFormats.delete('insertUnorderedList');
      this.activeFormats.delete('insertOrderedList');
      this.activeFormats.delete('blockquote');
      this.activeFormats.delete('pre');
      this.blockType = 'p';
    }

    if (shouldActivate) {
      this.activeFormats.add(format);
    } else {
      this.activeFormats.delete(format);
    }

    this.renderEmptyFormattingPreview();
  }

  private updateEmptyBlockState(block: string): void {
    this.activeFormats.delete('insertUnorderedList');
    this.activeFormats.delete('insertOrderedList');
    this.activeFormats.delete('blockquote');
    this.activeFormats.delete('pre');
    this.blockType = block;

    if (block === 'blockquote' || block === 'pre') {
      this.activeFormats.add(block);
    }

    this.renderEmptyFormattingPreview();
  }

  private insertTextWithPendingFormatting(text: string): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return;

    const textNode = this.document.createTextNode(text);
    editor.replaceChildren(createFormattedComposerContent(this.document, textNode, this.activeFormats, this.blockType));
    placeComposerCaretAfter(this.document, textNode);
    this.value = readComposerValue(editor);
    this.emitValue();
    this.refreshFormatState();
  }

  private insertText(text: string): void {
    const availableLength = Math.max(0, this.maxLength - this.value.length);
    const insertedText = text.slice(0, availableLength);
    if (!insertedText) return;

    if (!this.value && this.hasPendingFormatting) {
      this.insertTextWithPendingFormatting(insertedText);
      return;
    }

    this.focusEditor();
    this.document.execCommand('insertText', false, insertedText);
    this.syncAfterCommand();
  }

  private renderEmptyFormattingPreview(): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor || this.value) return;

    if (!this.hasPendingFormatting) {
      editor.textContent = '';
      placeComposerCaretAtEnd(this.document, editor);
      return;
    }

    const marker = this.document.createTextNode(EMPTY_EDITOR_MARKER);
    editor.replaceChildren(createFormattedComposerContent(this.document, marker, this.activeFormats, this.blockType));
    placeComposerCaretAfter(this.document, marker);
  }

  private emitValue(): void {
    this.onChange(this.value);
  }

  private clearAttachments(emit = true): void {
    this.releaseAttachments();
    this.attachments = [];
    this.attachmentError = '';
    if (emit) this.emitAttachments();
  }

  private releaseAttachments(): void {
    this.attachments.forEach(item => this.revokePreviewUrl(item.previewUrl));
  }

  private emitAttachments(): void {
    this.attachmentsChange.emit(this.attachments.map(item => item.file));
  }

  private clearPersistedAttachments(): void {
    if (!this.persistedAttachments.length) return;

    this._persistedAttachments = [];
    this.persistedAttachmentsChange.emit([]);
  }

  private isAcceptedFile(file: File): boolean {
    if (!this.allowAnyFile) return file.type.startsWith('image/');

    const acceptedTypes = this.fileAccept
      .split(',')
      .map(value => value.trim().toLowerCase())
      .filter(Boolean);
    if (!acceptedTypes.length) return true;

    const contentType = (file.type || '').toLowerCase();
    return acceptedTypes.some(acceptedType => {
      if (acceptedType.startsWith('.')) return file.name.toLowerCase().endsWith(acceptedType);
      if (acceptedType.endsWith(WILDCARD_CONTENT_TYPE_SUFFIX)) {
        return contentType.startsWith(acceptedType.slice(0, -1));
      }
      return contentType === acceptedType;
    });
  }

  private revokePreviewUrl(previewUrl: string | null): void {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }

  attachmentIconName(contentType: string): string {
    return resolveAttachmentIcon(contentType) ?? 'icon_document';
  }
}
